import React from 'react'
import { cn } from '@/utils/ui'
import { Icon } from '@/ui/atoms/Icon'

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  title?: string
  iconName?: string
  size?: 'small' | 'medium' | 'large'
  variant?:
    | 'neutral'
    | 'blue'
    | 'light-blue'
    | 'subtle-blue'
    | 'error'
    | 'success'
    | 'warning'
    | 'transparent'
    | 'transparentInverted'
    | 'orange'
    | 'plain'
  colorScheme?: string
  className?: string
}

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ title, iconName, colorScheme, size = 'medium', variant, className, ...props }, ref) => {
    return (
      <div
        style={
          colorScheme
            ? {
                backgroundColor: `${colorScheme.split('-')[0]}`,
                color: `${colorScheme.split('-')[1]}`,
              }
            : {}
        }
        className={cn(
          'inline-flex justify-center items-center gap-1',
          variant !== 'plain' && 'rounded-full',
          {
            'min-h-6 py-1 px-2': size === 'small',
            'min-h-7 py-1.5 px-2': size === 'medium',
            'min-h-9 py-2 px-3': size === 'large',
            // Variant styles
            'bg-neutral-90 text-text-base': variant === 'neutral',
            'bg-blue-95 text-blue-20': variant === 'blue',
            'bg-blue-97 text-blue-20': variant === 'light-blue',
            'bg-bg-subtle-blue text-blue-20': variant === 'subtle-blue',
            'bg-red-95 text-red-20': variant === 'error',
            'bg-green-90 text-green-20': variant === 'success',
            'bg-yellow-90 text-yellow-20': variant === 'warning',
            'bg-bg-base-transparent-80 text-text-base': variant === 'transparent',
            'bg-bg-intense-transparent-50 text-text-inverted backdrop-blur':
              variant === 'transparentInverted',
            'bg-orange-90 text-orange-30': variant === 'orange',
          },
          className,
        )}
      >
        {iconName && <Icon name={iconName} className="w-4 h-4" />}
        {title && (
          <span
            className={cn('leading-none', {
              'text-label-XS-regular': size === 'small' || size === 'medium',
              'text-label-S-regular': size === 'large',
              '!text-body-S-semibold text-text-base': variant === 'plain',
            })}
          >
            {title}
          </span>
        )}
      </div>
    )
  },
)

Label.displayName = 'Label'
